import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppShell, Content } from '../../components/ui/AppShell';
import { DesktopColumns } from '../../components/ui/DesktopColumns';
import { LeagueSwitcher } from '../../components/ui/LeagueSwitcher';
import { MiniLeagueCard } from '../../components/miniLeagues/LeagueCard';
import { Plus, UserPlus } from '../../components/ui/icons';
import { useHaptics } from '../../hooks/useHaptics';
import { useAuth } from '../../context/AuthContext';
import {
  useLeagues,
  useMyDuels,
  useMiniLeagues,
  useFriendsSeason,
  useUsers,
  useAddFriend,
  useRespondToChallenge
} from '../../hooks/useQueries';
import { identityFor } from '../../data/leagues';
import { CreateMiniLeagueEntry } from './CreateMiniLeagueEntry';
import { createMiniLeaguePath, type MiniLeagueNavState } from '../leagues/miniLeagueHelpers';
import { DuelRow } from './DuelRow';
import { FriendsBoard } from './FriendsBoard';
import { IncomingChallenges } from './IncomingChallenges';
import { InlineForm } from './InlineForm';
import { Section } from './Section';

export default function FriendsScreen() {
  const navigate = useNavigate();
  const { impact } = useHaptics();
  const { leagueId } = useParams<{ leagueId: string }>();
  const { uid } = useAuth();
  const { data: leagues } = useLeagues();

  // Friends and duels are scoped to the league you're inside; mini-leagues span
  // every competition you've joined one in — but only within the SAME SPORT (a
  // cycling table shouldn't surface while you're looking at a football world).
  const league = leagues?.find((l) => l.leagueId === leagueId);
  const leagueName = league?.name ?? identityFor(leagueId ?? '')?.name ?? 'your league';
  const leagueLabel = (id: string) =>
    leagues?.find((l) => l.leagueId === id)?.name ?? identityFor(id)?.name ?? 'League';
  const sportOf = (id: string | undefined): string | undefined =>
    (id ? leagues?.find((l) => l.leagueId === id)?.sport : undefined) ??
    (id ? identityFor(id)?.sport : undefined);
  const currentSport = sportOf(leagueId);
  // Duels work in every sport — football compares market picks, cycling compares
  // binary duel calls — so the challenge entry is available in any league.
  const canChallenge = !!leagueId;

  const { data: duels } = useMyDuels(uid, leagueId);
  const { data: miniLeaguesRaw } = useMiniLeagues(uid);
  const miniLeagues = useMemo(() => {
    let list = miniLeaguesRaw ?? [];
    // Keep only same-sport tables (e.g. hide cycling leagues under a football world).
    if (currentSport) list = list.filter((ml) => sportOf(ml.leagueId) === currentSport);
    if (!leagueId) return list;
    // Current competition first, then the rest in creation order.
    return [...list].sort((a, b) => {
      const aHere = a.leagueId === leagueId ? 0 : 1;
      const bHere = b.leagueId === leagueId ? 0 : 1;
      if (aHere !== bHere) return aHere - bHere;
      return a.createdAt - b.createdAt;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miniLeaguesRaw, leagueId, currentSport, leagues]);
  const { data: friends } = useFriendsSeason(uid, leagueId);

  const incoming = useMemo(
    () => (duels ?? []).filter((d) => d.status === 'PENDING' && d.opponentId === uid),
    [duels, uid]
  );
  const liveDuels = useMemo(
    () => (duels ?? []).filter((d) => d.status === 'ACTIVE' || d.status === 'RESOLVED'),
    [duels, uid]
  );

  const challengerIds = incoming.map((d) => d.challengerId);
  const { data: users } = useUsers(challengerIds);

  const respond = useRespondToChallenge(uid);
  const addFriend = useAddFriend(uid);

  const [showAdd, setShowAdd] = useState(false);
  const [handle, setHandle] = useState('');

  const miniNavState: MiniLeagueNavState = { from: 'friends', leagueId: leagueId ?? undefined };

  return (
    <AppShell>
      <Content width="wide">
        <div className="pt-2 md:pt-5">
          <LeagueSwitcher leagueId={leagueId!} tab="friends" size="sm" />
        </div>
        <div className="flex items-center justify-between pt-3 md:pt-5">
          <h1 className="title text-[30px] md:text-[40px]">Friends</h1>
          <button
            type="button"
            onClick={() => {
              impact('light');
              setShowAdd((v) => !v);
            }}
            aria-label="Add a friend"
            className="tap grid h-10 w-10 place-items-center rounded-full border border-black/5 bg-card shadow-soft"
          >
            <UserPlus width={20} height={20} strokeWidth={2} className="text-ink" />
          </button>
        </div>

        {showAdd && (
          <InlineForm
            placeholder="Friend’s @username"
            value={handle}
            onChange={setHandle}
            cta="Add"
            busy={addFriend.isPending}
            error={addFriend.isError ? 'No player matches that handle.' : undefined}
            onSubmit={() => {
              if (!handle.trim()) return;
              addFriend.mutate(
                { username: handle.trim() },
                {
                  onSuccess: () => {
                    setHandle('');
                    setShowAdd(false);
                  }
                }
              );
            }}
          />
        )}

        <DesktopColumns
          asideWidth="360px"
          main={
            <>
        {/* incoming challenges */}
        <IncomingChallenges
          incoming={incoming}
          users={users}
          respondPending={respond.isPending}
          onRespond={(duelId, accept) => {
            respond.mutate(
              { duelId, accept },
              accept ? { onSuccess: () => navigate(`/duel/${duelId}`) } : undefined
            );
          }}
        />

        {/* active / resolved duels */}
        {liveDuels.length > 0 && (
          <Section title="Your duels">
            <div className="flex flex-col gap-[10px]">
              {liveDuels.map((d) => (
                <DuelRow key={d.duelId} duel={d} uid={uid!} onTap={() => navigate(`/duel/${d.duelId}`)} />
              ))}
            </div>
          </Section>
        )}

        {/* mini leagues */}
        <Section
          title="Mini leagues"
          action={
            (miniLeagues ?? []).length > 0 ? (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  aria-label="Join with code"
                  onClick={() => {
                    impact('light');
                    navigate('/leagues/join', { state: miniNavState });
                  }}
                  className="tap rounded-full bg-fill2 px-2.5 py-1 text-[11px] font-bold text-ink2"
                >
                  Join
                </button>
                <button
                  type="button"
                  aria-label="Create a mini league"
                  onClick={() => {
                    impact('light');
                    navigate(createMiniLeaguePath(leagueId), { state: miniNavState });
                  }}
                  className="tap grid h-7 w-7 place-items-center rounded-full bg-fill2 text-ink"
                >
                  <Plus width={16} height={16} strokeWidth={2.4} />
                </button>
              </div>
            ) : undefined
          }
        >
          {(miniLeagues ?? []).length > 0 ? (
            <div className="flex flex-col gap-[11px]">
              {miniLeagues.map((ml) => (
                <MiniLeagueCard
                  key={ml.miniLeagueId}
                  mini={ml}
                  uid={uid}
                  leagueName={leagueLabel(ml.leagueId)}
                  onOpen={() => {
                    impact('light');
                    navigate(`/leagues/${ml.miniLeagueId}`, { state: miniNavState });
                  }}
                />
              ))}
            </div>
          ) : (
            <CreateMiniLeagueEntry
              leagueName={leagueName}
              onCreate={() => {
                impact('light');
                navigate(createMiniLeaguePath(leagueId), { state: miniNavState });
              }}
              onJoin={() => {
                impact('light');
                navigate('/leagues/join', { state: miniNavState });
              }}
            />
          )}
        </Section>
            </>
          }
          aside={
            <>
        {/* friends leaderboard */}
        <Section title="Friends leaderboard · season">
          <FriendsBoard
            rows={friends ?? []}
            leagueName={leagueName}
            canChallenge={canChallenge}
            onInvite={() => setShowAdd(true)}
          />
        </Section>
            </>
          }
        />
      </Content>
    </AppShell>
  );
}
